<template>
    <div class="relative mb-4 flex flex-col gap-2 text-slate-700">
        <vx-box class="relative flex p-4 gap-2 items-center flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3">
            <div class="w-[100px] shrink-0 order-1">
                <font-awesome-icon widthAuto :icon="faTurnDownRight" class="mr-2 text-sky-500"></font-awesome-icon>
                <span class="whitespace-nowrap text-slate-800 text-base font-extrabold">
                    {{ props.actionIndex === 0 ? 'Do' : 'Then do' }}
                </span>
            </div>
            <div data-test="action-wrapper" class="grow w-full order-2 lg:order-1 lg:w-auto">
                <vx-selectable
                    placeholder="automated action"
                    :reduce="(option) => option.value"
                    :getOptionLabel="getLabel"
                    v-model="action.automation"
                    :options="actions"
                    :clearable="false"
                    :error="displayFirstError(validate.automation)"
                >
                </vx-selectable>
            </div>

            <div class="flex items-center justify-end gap-2 ml-auto order-1 lg:ml-0 lg:order-2">
                <vx-toggle
                    :model-value="action.status === 'published'"
                    @update:model-value="action.status = $event ? 'published' : 'draft'"
                    size="md"
                >
                    <template v-slot:no>Off</template>
                    <template v-slot:yes>On</template>
                </vx-toggle>

                <vx-dropdown placement="right">
                    <vx-button size="md" type="button" color="muted-light">
                        <font-awesome-icon widthAuto :icon="faEllipsis"></font-awesome-icon>
                    </vx-button>
                    <template v-slot:items>
                        <vx-dropdown-item @click.prevent="duplicateAction(props.actionIndex)" type="button">
                            Duplicate
                            <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item @click.prevent="removeAction(props.actionIndex)" type="button">
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            :disabled="actionIndex === 0"
                            @click.prevent="moveAction(props.actionIndex, 'up')"
                            type="button"
                        >
                            Move Up
                            <font-awesome-icon widthAuto :icon="faArrowUp"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item
                            :disabled="actionIndex + 1 === data.automated_actions.length"
                            @click.prevent="moveAction(props.actionIndex, 'down')"
                            type="button"
                        >
                            Move Down
                            <font-awesome-icon widthAuto :icon="faArrowDown"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>
            </div>
        </vx-box>

        <vx-box v-show="showBox" class="relative p-4 ml-10 lg:ml-[122px] lg:p-6">
            <div
                class="absolute inset-bs-9 -inset-s-[25px] w-6 h-px border-solid border-y border-x-0 border-t-slate-300 border-b-white lg:-inset-s-20 lg:w-[78px]"
            ></div>

            <message-send v-if="action.automation == 'message_send'" v-model="action.data"> </message-send>

            <notify-message-action v-else-if="action.automation == 'notify_message_action'" v-model="action.data">
            </notify-message-action>

            <campaign-add-remove-contact
                v-else-if="
                    action.automation == 'campaign_add_contact' || action.automation === 'campaign_remove_contact'
                "
                :flowId="data.flow_id"
                :teamId="teamId"
                v-model="action.data"
            >
            </campaign-add-remove-contact>

            <contact-add-remove-group
                v-else-if="action.automation == 'contact_add_group' || action.automation == 'contact_remove_group'"
                :automation="action.automation"
                v-model="action.data"
            >
            </contact-add-remove-group>

            <contact-add-remove-tag
                v-else-if="action.automation == 'contact_add_tag' || action.automation == 'contact_remove_tag'"
                v-model="action.data"
            >
            </contact-add-remove-tag>

            <contact-add-update-custom-attribute
                v-else-if="action.automation == 'contact_add_update_custom_attribute'"
                v-model="action.data"
            >
            </contact-add-update-custom-attribute>

            <contact-remove-custom-attribute
                v-else-if="action.automation == 'contact_remove_custom_attribute'"
                v-model="action.data"
            >
            </contact-remove-custom-attribute>

            <invoke-api-destination
                v-else-if="action.automation == 'invoke_api_destination'"
                :event="data.event"
                v-model="action.data"
            >
            </invoke-api-destination>

            <contact-set-entry-channel
                v-else-if="action.automation == 'contact_set_entry_channel'"
                v-model="action.data"
            >
            </contact-set-entry-channel>

            <contact-set-lifecycle-status
                v-else-if="action.automation == 'contact_set_lifecycle_status'"
                v-model="action.data"
            >
            </contact-set-lifecycle-status>

            <contact-set-membership-status
                v-else-if="action.automation == 'contact_set_membership_status'"
                v-model="action.data"
            >
            </contact-set-membership-status>

            <raise-conversion-captured
                v-else-if="action.automation == 'raise_conversion_captured'"
                v-model="action.data"
            >
            </raise-conversion-captured>
        </vx-box>
    </div>
</template>

<script setup>
import { faArrowDown, faArrowUp, faCopy, faEllipsis, faTrash, faTurnDownRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxDropdown, VxDropdownItem, VxSelectable, VxToggle } from '@voxie/frontend-components';
import { computed } from 'vue';
import CampaignAddRemoveContact from '~/components/automation/automations/actions/CampaignAddRemoveContact.vue';
import ContactAddRemoveGroup from '~/components/automation/automations/actions/ContactAddRemoveGroup.vue';
import ContactAddRemoveTag from '~/components/automation/automations/actions/ContactAddRemoveTag.vue';
import ContactAddUpdateCustomAttribute from '~/components/automation/automations/actions/ContactAddUpdateCustomAttribute.vue';
import ContactRemoveCustomAttribute from '~/components/automation/automations/actions/ContactRemoveCustomAttribute.vue';
import ContactSetEntryChannel from '~/components/automation/automations/actions/ContactSetEntryChannel.vue';
import ContactSetLifecycleStatus from '~/components/automation/automations/actions/ContactSetLifecycleStatus.vue';
import ContactSetMembershipStatus from '~/components/automation/automations/actions/ContactSetMembershipStatus.vue';
import InvokeApiDestination from '~/components/automation/automations/actions/InvokeApiDestination.vue';
import MessageSend from '~/components/automation/automations/actions/MessageSend.vue';
import NotifyMessageAction from '~/components/automation/automations/actions/NotifyMessageAction.vue';
import RaiseConversionCaptured from '~/components/automation/automations/actions/RaiseConversionCaptured.vue';
import { useAutomationRule } from './useAutomationRule';
import { useAutomationRulesMetadata } from './useAutomationRulesMetadata';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';

const { actions } = useAutomationRulesMetadata();
const { removeAction, duplicateAction, moveAction, data } = useAutomationRule();

const action = defineModel();
const props = defineProps({
    actionIndex: {
        type: Number,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;

const contactSubscriptionAction = computed(() => {
    return [
        'contact_marketing_subscription_opt_in',
        'contact_marketing_subscription_opt_out',
        'contact_transactional_subscription_opt_in',
        'contact_transactional_subscription_opt_out',
    ].includes(action.value.automation);
});

const showBox = computed(() => {
    if (!action.value.automation) {
        return false;
    }
    if (contactSubscriptionAction.value) {
        return false;
    }

    if (
        [
            'contact_archive_messages',
            'contact_unsubscribe',
            'mark_as_handled',
            'contact_unset_entry_channel',
            'contact_unset_lifecycle_status',
            'contact_unset_membership_status',
        ].includes(action.value.automation)
    ) {
        return false;
    }

    return true;
});

const validate = useVuelidate(
    {
        automation: {
            required: helpers.withMessage('Action is required', required),
        },
    },
    action
);

const getLabel = (option) => {
    if (typeof option === 'object') {
        return option.label.toLowerCase();
    } else if (typeof option === 'string') {
        return option.toLowerCase();
    } else {
        return option;
    }
};
</script>
